import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Academy,
  AgeGroup,
  AcademySport,
  Lesson,
  Term,
  type Location,
  getPublicBookableTerms,
  type PublicProgramSummary,
  type ProRataPreviewResponse,
} from '@court-sync/shared';
import { Card, CardContent } from '../ui/Card';
import { Button } from '@/components/ui';
import { Badge } from '../ui/Badge';
import { isLessonBookable } from '../../utils/lessonBooking';
import { useFormatDate } from '../../hooks/useFormatDate';
import { LessonFilters, type LessonFiltersType } from './LessonFilters';
import WeeklySchedule from '../schedule/WeeklySchedule';
import { usePublicBookingStore } from '../../store/publicBooking';
import {
  appendStudentInviteQuery,
  cloneSearchParamsPreservingStudentInvite,
} from '../../pages/public/publicBooking/bookingPaths';
import {
  BookOpen,
  LayoutList,
  CalendarDays,
} from 'lucide-react';
import PublicAcademyMap from '../PublicAcademyMap';
import { PublicLessonBrowseItem } from './PublicLessonBrowseItem';
import {
  matchesPublicBrowseFilters,
  resolvePublicAgeGroupFilterId,
  resolvePublicProgramFilterId,
  type PublicBrowseFilterContext,
} from '../../utils/publicLessonBrowseFilters';
import { SportFilterBadges } from '@/components/sport/SportFilterBadges';
import { isStarterRequestBookingAcademy, resolvePublicSportFilterId } from '../../utils/publicBrowseMode';
import { StarterRequestBookingModal } from './StarterRequestBookingModal';
import { usePublicLessonBrowse } from '@/queries/publicLessonBrowse';
import {
  PUBLIC_LESSON_PAGE_SIZE,
  type PublicLessonBrowseParams,
} from '@/services/lesson';
import type { PaginationState } from '../ui/PaginatedTable';

const shortDateOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

type BrowseViewMode = 'list' | 'schedule';

const LIST_CLASS = 'flex flex-col gap-3';

function BrowsePagination({
  pagination,
  onPageChange,
  scrollTargetRef,
}: {
  pagination: PaginationState | null;
  onPageChange: (page: number) => void;
  scrollTargetRef?: React.RefObject<HTMLElement | null>;
}) {
  if (!pagination || pagination.totalPages <= 1) return null;

  const from = (pagination.page - 1) * pagination.limit + 1;
  const to = Math.min(pagination.page * pagination.limit, pagination.total);

  const changePage = (page: number) => {
    onPageChange(page);
    scrollTargetRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-muted-foreground">
        Showing {from}–{to} of {pagination.total}
      </p>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          Page {pagination.page} of {pagination.totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => changePage(Math.max(1, pagination.page - 1))}
          disabled={pagination.page <= 1}
        >
          Previous
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => changePage(pagination.page + 1)}
          disabled={pagination.page >= pagination.totalPages}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

export interface PublicLessonBrowserProps {
  academy: Academy;
  academySlug: string;
  lessons: Lesson[];
  terms: Term[];
  academySports: AcademySport[];
  ageGroups: AgeGroup[];
  publicLocations: Location[];
  publicPrograms: PublicProgramSummary[];
  proRataPreviewByLessonId: Record<string, ProRataPreviewResponse | undefined>;
  resolvedManualInviteToken: string | null;
  onBookLesson: (lessonId: string) => void;
  buildAcademyPath: (subpath: string) => string;
  /** When set (e.g. /location/:id route), locks location filter. */
  lockedLocationId?: string;
  /** Embedded on academy home — skip navigate-to-/lessons on reset and hide camps "View all". */
  embeddedOnAcademyHome?: boolean;
  /** When set, list view fetches paginated lessons for this type from the server. */
  paginatedScope?: 'group' | 'camp';
  showLocationMap?: boolean;
  className?: string;
}

export function PublicLessonBrowser({
  academy,
  academySlug: _academySlug,
  lessons,
  terms,
  academySports,
  ageGroups,
  publicLocations,
  publicPrograms,
  proRataPreviewByLessonId,
  resolvedManualInviteToken,
  onBookLesson,
  buildAcademyPath,
  lockedLocationId,
  embeddedOnAcademyHome = false,
  paginatedScope,
  showLocationMap = true,
  className,
}: PublicLessonBrowserProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const manualInviteToken = usePublicBookingStore((s) => s.manualInviteToken);
  const { formatDate } = useFormatDate({ region: academy.region, publicContext: true });
  const isMultiSport = academySports.length > 1;
  const starterRequestBooking = isStarterRequestBookingAcademy(academy);

  const bookableTerms = useMemo(() => getPublicBookableTerms(terms, new Date()), [terms]);
  const bookableTermById = useMemo(
    () => new Map(bookableTerms.map((term) => [term.id, term])),
    [bookableTerms]
  );

  const [filters, setFilters] = useState<LessonFiltersType>({});
  const [requestLesson, setRequestLesson] = useState<Lesson | null>(null);
  const [viewMode, setViewMode] = useState<BrowseViewMode>('list');
  const [groupPage, setGroupPage] = useState(1);
  const [campPage, setCampPage] = useState(1);
  const browseSectionRef = useRef<HTMLElement>(null);

  const usePaginatedBrowse =
    viewMode !== 'schedule' && (paginatedScope !== undefined || !embeddedOnAcademyHome);
  const showGroupSection = paginatedScope !== 'camp';
  const showCampSection = paginatedScope !== 'group';

  const showMultiLocationPublic = publicLocations.length >= 2;

  const selectedProgramId = useMemo(() => {
    const p = searchParams.get('program');
    return !p || p === 'all' ? 'all' : p;
  }, [searchParams]);

  const selectedAgeGroupId = useMemo(() => {
    const id = searchParams.get('ageGroupId');
    return !id || id === 'all' ? 'all' : id;
  }, [searchParams]);

  const selectedSportId = useMemo(() => {
    const id = searchParams.get('sport');
    return !id || id === 'all' ? 'all' : id;
  }, [searchParams]);

  const resolvedProgramFilterId = useMemo(
    () => resolvePublicProgramFilterId(searchParams, publicPrograms.map((p) => p.id)),
    [searchParams, publicPrograms]
  );

  const resolvedAgeGroupFilterId = useMemo(
    () => resolvePublicAgeGroupFilterId(searchParams, ageGroups),
    [searchParams, ageGroups]
  );

  const resolvedSportFilterId = useMemo(
    () => resolvePublicSportFilterId(searchParams, academySports.map((s) => s.id)),
    [searchParams, academySports]
  );

  const browseFilterContext = useMemo(
    (): PublicBrowseFilterContext => ({
      filters,
      programId: resolvedProgramFilterId,
      ageGroupId: resolvedAgeGroupFilterId,
      academySportId: resolvedSportFilterId,
      ageGroups,
      academySports,
    }),
    [filters, resolvedProgramFilterId, resolvedAgeGroupFilterId, resolvedSportFilterId, ageGroups, academySports]
  );

  const browseApiBase = useMemo((): Omit<PublicLessonBrowseParams, 'page' | 'lessonType'> => ({
    limit: PUBLIC_LESSON_PAGE_SIZE,
    termId: filters.termId,
    locationId: lockedLocationId ?? filters.locationId,
    search: filters.searchQuery,
    programId: resolvedProgramFilterId ?? undefined,
    ageGroupId: resolvedAgeGroupFilterId ?? undefined,
    sportId: resolvedSportFilterId ?? undefined,
    dayOfWeek: filters.dayOfWeek,
    skillLevel: filters.skillLevel,
    ageGroup: filters.ageGroup,
    maxPrice: filters.maxPrice,
  }), [
    filters,
    lockedLocationId,
    resolvedProgramFilterId,
    resolvedAgeGroupFilterId,
    resolvedSportFilterId,
  ]);

  const groupBrowseParams = useMemo(
    (): PublicLessonBrowseParams => ({
      ...browseApiBase,
      page: groupPage,
      lessonType: 'group',
    }),
    [browseApiBase, groupPage]
  );

  const campBrowseParams = useMemo(
    (): PublicLessonBrowseParams => ({
      ...browseApiBase,
      page: campPage,
      lessonType: 'camp',
    }),
    [browseApiBase, campPage]
  );

  const { data: groupBrowseResult, isLoading: groupBrowseLoading, isFetching: groupBrowseFetching } =
    usePublicLessonBrowse(academy.id, groupBrowseParams, {
      enabled: usePaginatedBrowse && showGroupSection,
    });

  const { data: campBrowseResult, isLoading: campBrowseLoading, isFetching: campBrowseFetching } =
    usePublicLessonBrowse(academy.id, campBrowseParams, {
      enabled: usePaginatedBrowse && showCampSection,
    });

  useEffect(() => {
    setGroupPage(1);
    setCampPage(1);
  }, [browseApiBase, paginatedScope]);

  const paginatedGroupLessons = groupBrowseResult?.data ?? [];
  const paginatedCampLessons = campBrowseResult?.data ?? [];
  const groupPagination = groupBrowseResult?.pagination ?? null;
  const campPagination = campBrowseResult?.pagination ?? null;

  useEffect(() => {
    if (!showMultiLocationPublic || !lockedLocationId) return;
    if (publicLocations.some((l) => l.id === lockedLocationId)) {
      setFilters((prev) =>
        prev.locationId === lockedLocationId ? prev : { ...prev, locationId: lockedLocationId }
      );
    }
  }, [showMultiLocationPublic, publicLocations, lockedLocationId]);

  const navigateToAllPublicLessons = useCallback(() => {
    navigate(
      appendStudentInviteQuery(buildAcademyPath('/lessons'), resolvedManualInviteToken),
      { replace: true }
    );
  }, [navigate, buildAcademyPath, resolvedManualInviteToken]);

  const setProgramInUrl = useCallback(
    (programId: string | 'all') => {
      const next = cloneSearchParamsPreservingStudentInvite(searchParams, manualInviteToken);
      if (programId === 'all') {
        next.delete('program');
      } else {
        next.set('program', programId);
      }
      navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
    },
    [navigate, location.pathname, searchParams, manualInviteToken]
  );

  const setAgeGroupInUrl = useCallback(
    (ageGroupId: string | 'all') => {
      const next = cloneSearchParamsPreservingStudentInvite(searchParams, manualInviteToken);
      if (ageGroupId === 'all') {
        next.delete('ageGroupId');
      } else {
        next.set('ageGroupId', ageGroupId);
      }
      navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
    },
    [navigate, location.pathname, searchParams, manualInviteToken]
  );

  const setSportInUrl = useCallback(
    (academySportId: string | 'all') => {
      const next = cloneSearchParamsPreservingStudentInvite(searchParams, manualInviteToken);
      if (!academySportId || academySportId === 'all') {
        next.delete('sport');
      } else {
        next.set('sport', academySportId);
      }
      navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
    },
    [navigate, location.pathname, searchParams, manualInviteToken]
  );

  const setViewModeAndClearDay = useCallback((mode: BrowseViewMode) => {
    setViewMode(mode);
    if (mode === 'schedule') {
      setFilters((f) => (f.dayOfWeek !== undefined ? { ...f, dayOfWeek: undefined } : f));
    }
  }, []);

  useEffect(() => {
    const next = cloneSearchParamsPreservingStudentInvite(searchParams, manualInviteToken);
    let changed = false;

    const pid = searchParams.get('program');
    if (pid && pid !== 'all') {
      if (publicPrograms.length === 0 || !publicPrograms.some((p) => p.id === pid)) {
        next.delete('program');
        changed = true;
      }
    }

    const ageGroupParam = searchParams.get('ageGroupId');
    if (ageGroupParam && ageGroupParam !== 'all') {
      if (ageGroups.length === 0 || !ageGroups.some((g) => g.id === ageGroupParam)) {
        next.delete('ageGroupId');
        changed = true;
      }
    }

    const sportParam = searchParams.get('sport');
    if (sportParam && sportParam !== 'all') {
      if (academySports.length === 0 || !academySports.some((s) => s.id === sportParam)) {
        next.delete('sport');
        changed = true;
      }
    }

    if (changed) {
      navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
    }
  }, [searchParams, publicPrograms, ageGroups, academySports, navigate, location.pathname, manualInviteToken]);

  useEffect(() => {
    const raw = location.hash.replace(/^#/, '');
    if (raw.startsWith('lesson-')) {
      setViewMode('list');
    }
  }, [location.hash]);

  useEffect(() => {
    if (!starterRequestBooking || searchParams.get('request') !== '1') return undefined;
    const raw = location.hash.replace(/^#/, '');
    if (!raw.startsWith('lesson-')) return undefined;
    const lessonId = raw.slice('lesson-'.length);
    const lesson = lessons.find((l) => l.id === lessonId);
    if (!lesson) return undefined;
    setViewMode('list');
    const t = window.setTimeout(() => {
      setRequestLesson(lesson);
      document.getElementById(raw)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => window.clearTimeout(t);
  }, [starterRequestBooking, searchParams, location.hash, lessons]);

  const scopedLessons = useMemo(() => {
    return lessons.filter((lesson) => {
      if (lesson.lessonType === 'private') return false;
      if (lesson.lessonType === 'camp') {
        return lesson.isActive !== false && isLessonBookable(lesson, null);
      }
      if (lesson.termId) {
        const term = bookableTermById.get(lesson.termId);
        if (!term) return false;
        return isLessonBookable(lesson, term);
      }
      return false;
    });
  }, [lessons, bookableTermById]);

  const filteredLessons = useMemo(
    () => scopedLessons.filter((lesson) => matchesPublicBrowseFilters(lesson, browseFilterContext)),
    [scopedLessons, browseFilterContext]
  );

  const filteredNonCampLessons = useMemo(
    () =>
      usePaginatedBrowse
        ? paginatedGroupLessons
        : scopedLessons.filter((lesson) => matchesPublicBrowseFilters(lesson, browseFilterContext)),
    [usePaginatedBrowse, paginatedGroupLessons, scopedLessons, browseFilterContext]
  );

  const hubCamps = useMemo(
    () =>
      usePaginatedBrowse
        ? paginatedCampLessons
        : filteredLessons.filter(
            (l) =>
              l.lessonType === 'camp' &&
              l.isActive !== false &&
              isLessonBookable(l, null)
          ),
    [usePaginatedBrowse, paginatedCampLessons, filteredLessons]
  );

  const filteredScheduleLessons = useMemo(() => {
    const scheduleSource = filteredLessons;
    const byId = new Map<string, Lesson>();
    for (const lesson of scheduleSource) {
      byId.set(lesson.id, lesson);
    }
    return [...byId.values()];
  }, [filteredLessons]);

  const locationNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const loc of publicLocations) {
      m[loc.id] = loc.name;
    }
    return m;
  }, [publicLocations]);

  const hasVisibleCamps = showCampSection && (campPagination?.total ?? hubCamps.length) > 0;
  const hasGroupResults = showGroupSection && (groupPagination?.total ?? filteredNonCampLessons.length) > 0;
  const hasBrowseResults =
    hasGroupResults || hasVisibleCamps || (viewMode === 'schedule' && filteredScheduleLessons.length > 0);
  const listLoading =
    usePaginatedBrowse &&
    ((showGroupSection && (groupBrowseLoading || groupBrowseFetching)) ||
      (showCampSection && (campBrowseLoading || campBrowseFetching)));

  const handleResetFilters = useCallback(() => {
    setFilters({});
    setProgramInUrl('all');
    setAgeGroupInUrl('all');
    setSportInUrl('all');
    if (showMultiLocationPublic && !embeddedOnAcademyHome) {
      navigateToAllPublicLessons();
    }
  }, [
    setProgramInUrl,
    setAgeGroupInUrl,
    setSportInUrl,
    showMultiLocationPublic,
    embeddedOnAcademyHome,
    navigateToAllPublicLessons,
  ]);

  useEffect(() => {
    if (viewMode === 'schedule') return undefined;
    const raw = location.hash.replace(/^#/, '');
    if (!raw.startsWith('lesson-')) return undefined;
    const t = window.setTimeout(() => {
      document.getElementById(raw)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    return () => window.clearTimeout(t);
  }, [location.hash, filteredLessons, viewMode]);

  const locationMapSection = useMemo(() => {
    if (!showLocationMap || !showMultiLocationPublic || !filters.locationId) return null;
    const loc = publicLocations.find((l) => l.id === filters.locationId);
    const addr = loc?.address || loc?.googleMapsAddress;
    if (!loc || !addr) return null;
    return (
      <div className="mb-10 mt-12">
        <h2 className="mb-4 text-xl font-bold text-foreground">Map — {loc.name}</h2>
        <PublicAcademyMap address={addr} name={loc.name} />
      </div>
    );
  }, [showLocationMap, showMultiLocationPublic, filters.locationId, publicLocations]);

  const accentColor = academy.primaryColor;

  const scrollToContact = useCallback(() => {
    const contactEl =
      document.getElementById('public-academy-contact') ??
      document.querySelector('footer');
    contactEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const contactAcademy = useCallback(() => {
    if (academy.email) {
      window.location.href = `mailto:${academy.email}`;
      return;
    }
    if (academy.phone) {
      window.location.href = `tel:${academy.phone}`;
      return;
    }
    scrollToContact();
  }, [academy.email, academy.phone, scrollToContact]);

  const openRequestBooking = useCallback((lesson: Lesson) => {
    setRequestLesson(lesson);
  }, []);

  const handleLessonAction = useCallback(
    (lessonId: string) => {
      if (starterRequestBooking) {
        const lesson = lessons.find((l) => l.id === lessonId);
        if (lesson) openRequestBooking(lesson);
        return;
      }
      onBookLesson(lessonId);
    },
    [starterRequestBooking, lessons, openRequestBooking, onBookLesson]
  );

  const renderBrowseItem = (lesson: Lesson, bookLabel = 'Book Lesson') => {
    const term = lesson.termId ? terms.find((t) => t.id === lesson.termId) ?? null : null;
    return (
      <PublicLessonBrowseItem
        key={lesson.id}
        lesson={lesson}
        term={term}
        academy={academy}
        ageGroups={ageGroups}
        academySports={academySports}
        proRataPreview={proRataPreviewByLessonId[lesson.id] ?? lesson.proRataPreview}
        accentColor={accentColor}
        isMultiSport={isMultiSport}
        starterRequestBooking={starterRequestBooking}
        bookLabel={bookLabel}
        formatDate={formatDate}
        variant="list"
        onBook={() => onBookLesson(lesson.id)}
        onRequestBooking={() => openRequestBooking(lesson)}
        onContactAcademy={contactAcademy}
      />
    );
  };

  const renderCampBrowseItem = (lesson: Lesson) => (
    <PublicLessonBrowseItem
      key={lesson.id}
      lesson={lesson}
      term={null}
      academy={academy}
      ageGroups={ageGroups}
      academySports={academySports}
      proRataPreview={proRataPreviewByLessonId[lesson.id] ?? lesson.proRataPreview}
      accentColor={accentColor}
      isMultiSport={isMultiSport}
      starterRequestBooking={starterRequestBooking}
      bookLabel="Book camp"
      locationName={
        lesson.locationId && locationNameById[lesson.locationId]
          ? locationNameById[lesson.locationId]
          : null
      }
      formatDate={formatDate}
      variant="list"
      descriptionReadMoreTo={`${location.pathname}#lesson-${lesson.id}`}
      onBook={() => onBookLesson(lesson.id)}
      onRequestBooking={() => openRequestBooking(lesson)}
      onContactAcademy={contactAcademy}
    />
  );

  const renderLessonSections = (lessonsToRender: Lesson[]) => {
    const now = new Date();
    const lessonsByTerm = new Map<string, { term: Term; lessons: Lesson[] }>();
    const privateLessons: Lesson[] = [];

    lessonsToRender.forEach((lesson) => {
      if (!lesson.termId && lesson.lessonType === 'private') {
        privateLessons.push(lesson);
      } else if (lesson.termId) {
        const term = terms.find((t) => t.id === lesson.termId);
        if (term) {
          if (!lessonsByTerm.has(lesson.termId)) {
            lessonsByTerm.set(lesson.termId, { term, lessons: [] });
          }
          lessonsByTerm.get(lesson.termId)!.lessons.push(lesson);
        }
      }
    });

    const sortedTerms = Array.from(lessonsByTerm.values())
      .map((item) => item.term)
      .sort((a, b) => {
        const aStart = new Date(a.startDate);
        const bStart = new Date(b.startDate);
        const aEnd = new Date(a.endDate);
        const bEnd = new Date(b.endDate);
        const aIsActive = now >= aStart && now <= aEnd;
        const bIsActive = now >= bStart && now <= bEnd;
        if (aIsActive && !bIsActive) return -1;
        if (!aIsActive && bIsActive) return 1;
        return aStart.getTime() - bStart.getTime();
      });

    const containerClass = LIST_CLASS;
    const Container = 'ul';

    return (
      <div className="space-y-8 sm:space-y-10">
        {privateLessons.length > 0 && (
          <div>
            <h2 className="mb-3 text-lg font-bold text-foreground sm:mb-4 sm:text-xl">
              Private Lessons
            </h2>
            <Container className={containerClass}>
              {privateLessons.map((lesson) => renderBrowseItem(lesson, 'Book Lesson'))}
            </Container>
          </div>
        )}

        {sortedTerms.map((term) => {
          const termLessons = lessonsByTerm.get(term.id)?.lessons ?? [];
          if (termLessons.length === 0) return null;

          const startDate = new Date(term.startDate);
          const endDate = new Date(term.endDate);
          const isActive = now >= startDate && now <= endDate;
          const isUpcoming = startDate > now;

          return (
            <div key={term.id}>
              <div className="mb-3 flex flex-col gap-2 sm:mb-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-foreground sm:text-xl">{term.name}</h2>
                  <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
                    {formatDate(term.startDate, academy.region, shortDateOptions)} –{' '}
                    {formatDate(term.endDate, academy.region, shortDateOptions)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {isActive && (
                    <Badge variant="secondary" className="bg-green-600 text-white">
                      Current Term
                    </Badge>
                  )}
                  {isUpcoming && (
                    <Badge variant="secondary" className="bg-primary-600 text-white">
                      Upcoming
                    </Badge>
                  )}
                </div>
              </div>
              <Container className={containerClass}>
                {termLessons.map((lesson) => renderBrowseItem(lesson, 'Book Lesson'))}
              </Container>
            </div>
          );
        })}
        {locationMapSection}
      </div>
    );
  };

  const listContent = useMemo(
    () => renderLessonSections(filteredNonCampLessons),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- render helpers close over stable handlers
    [
      filteredNonCampLessons,
      terms,
      academy,
      ageGroups,
      proRataPreviewByLessonId,
      locationMapSection,
      formatDate,
      onBookLesson,
      viewMode,
    ]
  );

  return (
    <section ref={browseSectionRef} className={className} aria-labelledby="public-lesson-browser-heading">
      <h2 id="public-lesson-browser-heading" className="sr-only">
        Browse lessons
      </h2>

      <div className="mb-3 space-y-2">
        {isMultiSport && (
          <SportFilterBadges
            sports={academySports}
            value={resolvedSportFilterId ?? ''}
            onChange={(sportId) => setSportInUrl(sportId || 'all')}
            ariaLabel="Filter lessons by sport"
          />
        )}
        <LessonFilters
          filters={filters}
          onFiltersChange={setFilters}
          onReset={handleResetFilters}
          terms={bookableTerms}
          locations={publicLocations}
          hidePrivateLessonType
          hideDayOfWeek={viewMode === 'schedule'}
          hideTermFilter={bookableTerms.length <= 1}
          variant="minimal"
          sports={academySports}
          selectedSportId={selectedSportId}
          onSportChange={setSportInUrl}
          programs={publicPrograms}
          selectedProgramId={selectedProgramId}
          onProgramChange={setProgramInUrl}
          ageGroups={ageGroups}
          selectedAgeGroupId={selectedAgeGroupId}
          onAgeGroupChange={setAgeGroupInUrl}
          startSlot={
            <>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewModeAndClearDay('list')}
                className="h-8 shrink-0 gap-1.5 px-2.5 sm:px-3"
                aria-label="List view"
                aria-pressed={viewMode === 'list'}
              >
                <LayoutList className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="hidden sm:inline">List</span>
              </Button>
              <Button
                variant={viewMode === 'schedule' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewModeAndClearDay('schedule')}
                className="h-8 shrink-0 gap-1.5 px-2.5 sm:px-3"
                aria-label="Weekly view"
                aria-pressed={viewMode === 'schedule'}
              >
                <CalendarDays className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="hidden sm:inline">Weekly</span>
              </Button>
            </>
          }
        />
      </div>

      {listLoading ? (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      ) : hasBrowseResults ? (
        <>
          {filteredScheduleLessons.length > 0 && viewMode === 'schedule' ? (
            <>
              <WeeklySchedule
                lessons={filteredScheduleLessons}
                terms={terms}
                region={academy.region}
                onLessonClick={(lesson) => handleLessonAction(lesson.id)}
                allowEnrolmentWhenFull={!!academy.allowEnrolmentWhenFull}
                allowClickWhenFullForWaitlist={!!academy.waitlistEnabled}
              />
              {locationMapSection}
            </>
          ) : hasGroupResults && showGroupSection && viewMode === 'list' ? (
            <>
              {listContent}
              <BrowsePagination
                pagination={groupPagination}
                onPageChange={setGroupPage}
                scrollTargetRef={browseSectionRef}
              />
            </>
          ) : null}

          {hasVisibleCamps && viewMode === 'list' && (
            <div className={hasGroupResults && showGroupSection ? 'mt-12' : undefined}>
              <div className="mb-4 flex items-center justify-between sm:mb-6">
                {paginatedScope !== 'camp' ? (
                  <h2 className="text-xl font-bold text-foreground sm:text-2xl">Camps</h2>
                ) : (
                  <span />
                )}
              </div>
              <ul className={LIST_CLASS}>
                {hubCamps.map((lesson) => renderCampBrowseItem(lesson))}
              </ul>
              <BrowsePagination
                pagination={campPagination}
                onPageChange={setCampPage}
                scrollTargetRef={browseSectionRef}
              />
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <BookOpen className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
            <h3 className="mb-2 text-xl font-semibold text-foreground">No Lessons Found</h3>
            <p className="mb-6 text-muted-foreground">
              {lessons.length === 0
                ? 'No lessons are currently available for this academy.'
                : 'No lessons match your current filters. Try adjusting your search criteria.'}
            </p>
            {lessons.length > 0 && (
              <Button variant="outline" onClick={handleResetFilters}>
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}
      {requestLesson ? (
        <StarterRequestBookingModal
          open={!!requestLesson}
          onOpenChange={(open) => {
            if (!open) setRequestLesson(null);
          }}
          academy={academy}
          lesson={requestLesson}
          term={requestLesson.termId ? terms.find((t) => t.id === requestLesson.termId) ?? null : null}
        />
      ) : null}
    </section>
  );
}
